import { useEffect, useRef, useState } from 'react';
import AutocompleteInput, {
  AutocompleteInputProps,
  Describable,
} from '@peachjar/ui/dist/lib/components/Inputs/AutocompleteInput';
import { useAriaInvalid } from '@/hooks';
import styles from './Autocomplete.styles';

type Props<T extends Describable> = AutocompleteInputProps<T> & {
  id: string;
};
export const Autocomplete = <T extends Describable>({
  id,
  label,
  lookup,
  renderOption,
  onChange,
  value,
  error,
  ...props
}: Props<T>) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [activeOptionId, setActiveOptionId] = useState<string | null>(null);
  const comboboxRef = useRef<HTMLDivElement | null>(null);
  const listboxRef = useRef<HTMLDivElement | null>(null);

  const getInput = () => comboboxRef.current?.querySelector<HTMLInputElement>('input') ?? null;

  useEffect(() => {
    const input = getInput();
    if (!input) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      const options = listboxRef.current?.querySelectorAll('[role="option"]');
      if (!options || options.length === 0) return;

      const currentIndex = Array.from(options).findIndex(option => option.id === activeOptionId);

      switch (event.key) {
        case 'ArrowDown': {
          const nextIndex = (currentIndex + 1) % options.length;
          const nextOption = options[nextIndex];
          setActiveOptionId(nextOption.id);
          event.preventDefault();
          break;
        }
        case 'ArrowUp': {
          const prevIndex = (currentIndex - 1 + options.length) % options.length;
          const prevOption = options[prevIndex];
          setActiveOptionId(prevOption.id);
          event.preventDefault();
          break;
        }
        case 'Enter': {
          if (activeOptionId) {
            const selectedOption = Array.from(options).find(option => option.id === activeOptionId);
            if (selectedOption && selectedOption instanceof HTMLElement) {
              selectedOption.click();
            }
          }
          event.preventDefault();
          break;
        }
        case 'Escape': {
          setIsDropdownOpen(false);
          event.preventDefault();
          break;
        }
      }
    };

    const handleFocus = () => setIsDropdownOpen(true);
    const handleBlur = (event: FocusEvent) => {
      if (!comboboxRef.current?.contains(event.relatedTarget as Node)) {
        setIsDropdownOpen(false);
      }
    };

    input.addEventListener('keydown', handleKeyDown);
    input.addEventListener('focus', handleFocus);
    input.addEventListener('blur', handleBlur);

    return () => {
      input.removeEventListener('keydown', handleKeyDown);
      input.removeEventListener('focus', handleFocus);
      input.removeEventListener('blur', handleBlur);
    };
  }, [activeOptionId, id]);

  useEffect(() => {
    const input = getInput();
    const listbox = listboxRef.current;
    if (input && listbox) {
      input.setAttribute('role', 'combobox');
      input.setAttribute('aria-expanded', isDropdownOpen.toString());
      input.setAttribute('aria-haspopup', 'listbox');
      input.setAttribute('aria-autocomplete', 'list');
      input.setAttribute('aria-controls', listbox.id || `${id}-listbox`);
      input.setAttribute('aria-activedescendant', activeOptionId || '');
      if (typeof label === 'string') {
        input.setAttribute('aria-label', label);
      }

      listbox.id = listbox.id || `${id}-listbox`;
      listbox.setAttribute('role', 'listbox');
    }
  }, [isDropdownOpen, activeOptionId, id, label]);

  useAriaInvalid(getInput, error, [id]);

  return (
    <div ref={comboboxRef} className={styles.root}>
      <AutocompleteInput<T>
        {...props}
        value={value}
        label={label}
        error={error}
        lookup={lookup}
        renderOption={(choice, state) => {
          const optionId = `${id}-option-${choice.description}`;
          return (
            <div
              id={optionId}
              role="option"
              aria-selected={state.selected}
              onMouseEnter={() => setActiveOptionId(optionId)}
            >
              {renderOption(choice, state)}
            </div>
          );
        }}
        onChange={val => {
          onChange?.(val);
          setIsDropdownOpen(false);
        }}
        data-testid={id}
      />
      <div ref={listboxRef} data-testid={`${id}-listbox`} />
    </div>
  );
};
